import { useState, useEffect } from "react";
import "./App.css";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { Toaster } from "./components/ui/toaster";
import WhatsAppButton from "./components/WhatsAppButton";
import Analytics from "./components/Analytics";
import Home from "./pages/Home";
import Shop from "./pages/Shop";
import ProductDetail from "./pages/ProductDetail";
import About from "./pages/About";
import Contact from "./pages/Contact";
import Cart from "./pages/Cart";
import Checkout from "./pages/Checkout";
import OrderSuccess from "./pages/OrderSuccess";
import Admin from "./pages/Admin";
import Login from "./pages/Login";
import Account from "./pages/Account";
import AuthCallback from "./pages/AuthCallback";
import Reviews from "./pages/Reviews";

// Scroll to top on route change
function ScrollToTop() {
  const { pathname } = useLocation();
  
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  
  return null;
}

// Component to handle OAuth callback detection
function AuthCallbackHandler({ children, onLogin }) {
  const location = useLocation();
  
  // Check URL fragment for session_id - handle BEFORE normal routing
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback onLogin={onLogin} />;
  }
  
  return children;
}

// Main router component
function AppRoutes({ cart, setCart, user, setUser }) {
  // Load cart from localStorage on mount
  useEffect(() => {
    const savedCart = localStorage.getItem('cart');
    if (savedCart) {
      setCart(JSON.parse(savedCart));
    }
  }, [setCart]);

  // Save cart to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem('cart', JSON.stringify(cart));
  }, [cart]);

  const handleAddToCart = (product) => {
    setCart(prevCart => {
      const existingItem = prevCart.find(item => item.id === product.id);
      if (existingItem) {
        return prevCart.map(item =>
          item.id === product.id
            ? { ...item, quantity: item.quantity + 1 }
            : item
        );
      }
      return [...prevCart, { ...product, quantity: 1 }];
    });
  };

  const handleUpdateQuantity = (productId, newQuantity) => {
    setCart(prevCart =>
      prevCart.map(item =>
        item.id === productId ? { ...item, quantity: newQuantity } : item
      )
    );
  };

  const handleRemoveFromCart = (productId) => {
    setCart(prevCart => prevCart.filter(item => item.id !== productId));
  };

  const handleClearCart = () => {
    setCart([]);
  };

  const handleLogin = (userData) => {
    setUser(userData);
  };

  const handleLogout = () => {
    setUser(null);
  };

  const cartCount = cart.reduce((sum, item) => sum + item.quantity, 0);

  return (
    <AuthCallbackHandler onLogin={handleLogin}>
      <Routes>
        <Route path="/" element={<Home cartCount={cartCount} onAddToCart={handleAddToCart} user={user} />} />
        <Route path="/shop" element={<Shop cartCount={cartCount} onAddToCart={handleAddToCart} />} />
        <Route path="/product/:id" element={<ProductDetail cartCount={cartCount} onAddToCart={handleAddToCart} />} />
        <Route path="/about" element={<About cartCount={cartCount} />} />
        {/* Redirect old URLs to correct ones */}
        <Route path="/about-us" element={<Navigate to="/about" replace />} />
        <Route path="/about-us/" element={<Navigate to="/about" replace />} />
        <Route path="/contact" element={<Contact cartCount={cartCount} />} />
        <Route
          path="/cart"
          element={
            <Cart
              cart={cart}
              cartCount={cartCount}
              onUpdateQuantity={handleUpdateQuantity}
              onRemoveFromCart={handleRemoveFromCart}
            />
          }
        />
        <Route
          path="/checkout"
          element={<Checkout cart={cart} cartCount={cartCount} onClearCart={handleClearCart} user={user} />}
        />
        <Route
          path="/order-success"
          element={<OrderSuccess cartCount={cartCount} onClearCart={handleClearCart} />}
        />
        <Route path="/admin" element={<Admin cartCount={cartCount} />} />
        <Route path="/login" element={<Login cartCount={cartCount} onLogin={handleLogin} />} />
        <Route path="/account" element={<Account cartCount={cartCount} onLogout={handleLogout} />} />
        <Route path="/auth/callback" element={<AuthCallback onLogin={handleLogin} />} />
        <Route path="/reviews" element={<Reviews cartCount={cartCount} />} />
      </Routes>
    </AuthCallbackHandler>
  );
}

function App() {
  const [cart, setCart] = useState([]);
  const [user, setUser] = useState(() => {
    const storedUser = localStorage.getItem('user');
    return storedUser ? JSON.parse(storedUser) : null;
  });

  return (
    <HelmetProvider>
      <div className="App">
        <BrowserRouter>
          <ScrollToTop />
          <Analytics />
          <AppRoutes cart={cart} setCart={setCart} user={user} setUser={setUser} />
          <WhatsAppButton />
          <Toaster />
        </BrowserRouter>
      </div>
    </HelmetProvider>
  );
}

export default App;
